import ReactDOM from "react-dom";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronUp, UserCircle, CreditCard } from "lucide-react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import errorCopy from "utils/error-copy";
import { useStripe } from "@stripe/react-stripe-js";
import Modal from "components/modal";
import PlanDetails from "sections/modal/plan-details";
import useModal from "utils/hooks/use-modal";
import ShimmerLoader from "components/shimmer-loader";
import ProfileModal from "components/profile-modal";
import { getUserByCogId, logoutCognitoUser } from "api/user";
import { clearOnboardingProgress } from "components/set-up-profile";
import { getSubscriptionData } from "api/subscriptions";
import { getUsageData } from "api/usage";
import { getAllStripeProducts, createCheckoutSession } from "api/stripe";
import { ReactComponent as EnterIcon } from "assets/svgs/enter.svg";
import useComponentClickOutside from "utils/hooks/use-component-click-outside";

const ProfileDropdown = () => {
  const navigate = useNavigate();
  const { isComponentVisible, toggleChild, wrapRef } = useComponentClickOutside();
  const [showProfileModal, setShowProfileModal] = useState(false);

  const { openModal: openPlanModal, closeModal: closePlanModal, isOpen: isPlanOpen } = useModal(false);

  const stripe = useStripe();
  const queryClient = useQueryClient();
  const [planLoading, setPlanLoading] = useState(null);

  const { data, isLoading } = useQuery("userProfile", getUserByCogId);
  const { data: stripeProducts } = useQuery("stripe-products", getAllStripeProducts, { retry: false });
  const { data: subscriptionData } = useQuery("userSubscription", getSubscriptionData, { retry: false });
  const { data: usageData } = useQuery("userUsage", getUsageData, { retry: false });

  const handleSignOut = async () => {
    try {
      const isSignedOut = await logoutCognitoUser();
      if (isSignedOut) {
        // Drop cached profile/onboarding state so the next user signing in on
        // this tab is evaluated by the onboarding gate from scratch.
        clearOnboardingProgress();
        queryClient.clear();
        navigate("/login");
      }
    } catch (error) {
      console.error("Failed signing out:", error);
    }
  };

  const handleCheckout = async (event, priceId) => {
    event.preventDefault();
    setPlanLoading(priceId);
    try {
      const response = await createCheckoutSession({ priceId });
      const result = await stripe.redirectToCheckout({ sessionId: response.id });
      if (result.error) {
        toast.error(errorCopy(result.error.message || "Payment failed"), { position: "top-right" });
      }
    } catch (error) {
      toast.error(errorCopy(error?.response?.data?.error || "That payment didn't go through."), { position: "top-right" });
    } finally {
      setPlanLoading(null);
    }
  };

  const getProfileInitials = () => {
    if (!data) return "";
    const first = data.firstName?.[0]?.toUpperCase() ?? "";
    const last = data.lastName?.[0]?.toUpperCase() ?? "";
    return first + last;
  };

  const getDisplayName = () => {
    if (!data) return "";
    const full = `${data.firstName ?? ""} ${data.lastName ?? ""}`.trim();
    return full.length > 22 ? full.substring(0, 20) + "…" : full;
  };

  return (
    <>
      <div ref={wrapRef} className="relative w-full">
        {/* Trigger. Not a card: the rail is already a surface, so a bordered box
            inside it read as a second panel bolted to the bottom. It is a row,
            taking a white overlay on hover and when open — the same signature
            the nav items use.

            Both call sites (the desktop rail and the mobile drawer) are on the
            dark ground, so this is styled for it directly rather than through a
            tone prop nothing would pass. */}
        <button
          type="button"
          onClick={toggleChild}
          className={`flex w-full cursor-pointer select-none items-center gap-3 rounded-ns px-2 py-2 text-left transition-colors duration-ui ease-ui hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-light ${
            isComponentVisible ? "bg-white/12" : ""
          }`}
          aria-expanded={isComponentVisible}
          aria-label="Account menu"
        >
          {/* Avatar. accent-light on deep, not accent — accent is barely
              separable from the rail behind it. */}
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent-light">
            {isLoading ? (
              <ShimmerLoader className="h-full w-full rounded-full" />
            ) : (
              <span className="font-body text-ui-xs font-medium leading-none text-deep">
                {getProfileInitials()}
              </span>
            )}
          </div>

          {/* Name over email — the email is what tells two logins apart */}
          <div className="min-w-0 flex-1">
            {isLoading ? (
              <>
                <ShimmerLoader className="h-3.5 w-24 rounded" />
                <ShimmerLoader className="mt-1.5 h-3 w-32 rounded" />
              </>
            ) : (
              <>
                <span className="block truncate font-body text-ui-sm font-medium leading-tight text-white">
                  {getDisplayName()}
                </span>
                {data?.email && (
                  <span className="mt-0.5 block truncate font-body text-meta leading-tight text-white/60">
                    {data.email}
                  </span>
                )}
              </>
            )}
          </div>

          {/* Chevron */}
          <ChevronUp
            size={15}
            className={`flex-shrink-0 text-white/60 transition-transform duration-ui ease-ui ${
              isComponentVisible ? "" : "rotate-180"
            }`}
          />
        </button>

        {/* Dropdown menu — opens upward */}
        {isComponentVisible && (
          <div className="absolute bottom-full left-0 z-[100] mb-2 w-full overflow-hidden rounded-ns border border-solid border-rule bg-white shadow-pop">
            <button
              onClick={() => {
                toggleChild();
                setShowProfileModal(true);
              }}
              className="flex w-full items-center gap-3 border-b border-solid border-rule px-4 py-3 font-body text-ui-sm text-muted transition-colors duration-ui ease-ui hover:bg-shade-50 hover:text-ink"
            >
              <UserCircle size={16} className="flex-shrink-0 text-muted" />
              <span>Profile</span>
            </button>

            <button
              onClick={() => {
                toggleChild();
                openPlanModal();
              }}
              className="flex w-full items-center gap-3 border-b border-solid border-rule px-4 py-3 font-body text-ui-sm text-muted transition-colors duration-ui ease-ui hover:bg-shade-50 hover:text-ink"
            >
              <CreditCard size={16} className="flex-shrink-0 text-accent" />
              <span className="text-accent">Upgrade Plan</span>
            </button>

            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-3 px-4 py-3 font-body text-ui-sm text-muted transition-colors duration-ui ease-ui hover:bg-shade-50 hover:text-ink"
            >
              <EnterIcon className="h-4 w-4 flex-shrink-0 text-muted" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Plan selection modal — Upgrade Plan */}
      <Modal isOpen={isPlanOpen} closeModal={closePlanModal}>
        <PlanDetails
          loading={planLoading}
          products={stripeProducts}
          userUsageData={usageData}
          subscriptionData={subscriptionData}
          handleClickGetStarted={handleCheckout}
        />
      </Modal>

      {/* Profile modal — portal renders outside transformed sidebar */}
      {showProfileModal && ReactDOM.createPortal(
        <ProfileModal onClose={() => setShowProfileModal(false)} />,
        document.body
      )}
    </>
  );
};

export default ProfileDropdown;
